import {
  BadgeCheckIcon,
  CalendarIcon,
  ChatAlt2Icon,
  CursorClickIcon,
  ExclamationCircleIcon,
  GiftIcon,
  GlobeAltIcon,
  IdentificationIcon,
  LightningBoltIcon,
  MicrophoneIcon,
  RefreshIcon,
} from '@heroicons/react/outline';
import { FaSquarespace } from 'react-icons/fa6';

import { useWebsiteFaqs } from './websiteShared';
import CodePanel from '../mocks/CodePanel';
import SiteChatDemo, { ChatButton } from '../mocks/SiteChatDemo';
import VoiceCall from '../mocks/VoiceCall';
import type { ChannelContent, DemoBrand } from '../types';
import { useChannelsT } from '../useChannelsT';

const BRAND: DemoBrand = {
  name: 'Olive & Thyme',
  initial: 'O',
  bg: 'bg-[#4d7c0f]',
};

/*
 * Facts this page relies on (checked 2026-10-21,
 * docs/agents/website-platforms.mdx#squarespace, voice.mdx): the snippet
 * goes into Code Injection → Footer; Code Injection is only on some
 * Squarespace plans, and a Code block holds the iframe for one page; voice
 * calls in the website chat are Unlimited 200+, Teams 200/300 and
 * Enterprise, with a Playground trial on other plans; calls use the same
 * knowledge and actions as the chat; about 1,900 voices.
 */
export function useSquarespaceContent(): ChannelContent {
  const { t } = useChannelsT();
  const websiteFaqs = useWebsiteFaqs();

  return {
    key: 'squarespace',
    name: 'Squarespace',
    logo: 'FaSquarespace',
    seoTitle: t(
      'Squarespace AI Chatbot: Add an AI Chat to Your Squarespace Site',
    ),
    seoDescription: t(
      'Add an AI chatbot to your Squarespace site with Code Injection. It answers visitors from your own content, takes bookings and captures leads, 24/7.',
    ),
    hero: {
      label: t('Squarespace AI chatbot'),
      title: t('Your Squarespace site, answering questions.'),
      accent: t('Around the clock.'),
      lead: t(
        'Paste one snippet into Code Injection and a chat bubble trained on your menu, pages and policies answers visitors on every page.',
      ),
      facts: [
        t('Works on the Free plan'),
        t('One snippet in Code Injection'),
        t('Your colors and icon'),
      ],
      description: t(
        'Example: a visitor on a restaurant’s Squarespace site asks about vegan options. The agent answers from the menu and shows a See the menu button.',
      ),
      demo: (
        <SiteChatDemo
          brand={BRAND}
          url='oliveandthyme.squarespace.com'
          site={{
            headline: t('Seasonal plates, made to share.'),
            picture:
              'bg-[linear-gradient(135deg,#bef264_0%,#4d7c0f_55%,#1a2e05_100%)]',
            accent: 'text-[#4d7c0f] dark:text-[#bef264]',
          }}
          lines={[
            {
              from: 'visitor',
              text: t('Do you have vegan options?'),
              at: 700,
            },
            {
              from: 'agent',
              content: t(
                'Yes! Half of our menu is vegan, including the roasted cauliflower and the dark chocolate tart.',
              ),
              typingAt: 1200,
              at: 2100,
            },
            {
              from: 'agent',
              content: <ChatButton brand={BRAND} label={t('See the menu')} />,
              at: 2700,
            },
          ]}
        />
      ),
      wide: true,
      steps: [],
    },
    jobs: {
      title: t('Turns visitors into customers.'),
      tail: t('Even when you’re offline.'),
      lead: t(
        'Visitors leave when they can’t find an answer. Your agent knows your pages, prices and policies, and answers in seconds, on every page.',
      ),
      items: [
        {
          icon: ChatAlt2Icon,
          title: t('Answers questions'),
          detail: t(
            'Opening hours, prices, services and policies, straight from your website and documents.',
          ),
        },
        {
          icon: CalendarIcon,
          title: t('Books appointments'),
          detail: t(
            'Checks when you’re free and books the time in your Google Calendar.',
          ),
        },
        {
          icon: MicrophoneIcon,
          title: t('Talks out loud'),
          detail: t(
            'On Unlimited 200 and up, visitors can talk to it by voice right in the chat.',
          ),
        },
        {
          icon: IdentificationIcon,
          title: t('Captures leads'),
          detail: t(
            'Collects names, emails and anything else you ask for, one question at a time, and saves them to Leads.',
          ),
        },
        {
          icon: CursorClickIcon,
          title: t('Shows buttons'),
          detail: t(
            'Sends visitors to booking, pricing or checkout pages with a button in the chat.',
          ),
        },
        {
          icon: GlobeAltIcon,
          title: t('Speaks their language'),
          detail: t(
            'Replies in the language each customer writes in, from the same knowledge.',
          ),
        },
      ],
    },
    setup: {
      title: t('Paste it into Code Injection.'),
      tail: t('One snippet, every page.'),
      steps: [
        {
          title: t('Copy the snippet'),
          detail: t(
            'In Easy-Peasy.AI, open your agent, go to Channels, then Chat bubble, click Manage and copy the install snippet.',
          ),
        },
        {
          title: t('Paste it into Footer'),
          detail: t(
            'In your Squarespace dashboard, open Code Injection, paste the snippet into the Footer field and click Save.',
          ),
        },
        {
          title: t('Check your live site'),
          detail: t('Open your site. The chat bubble appears on every page.'),
        },
      ],
      guide: 'https://easy-peasy.ai/docs/agents/website-platforms#squarespace',
      guideLabel: t('Read the Squarespace setup guide'),
      description: t(
        'Squarespace’s Code Injection settings with the install snippet in the Footer field, and a note that the chat shows on every page.',
      ),
      visual: (
        <CodePanel
          platform={
            <>
              <FaSquarespace className='h-5 w-5 text-[#0f0f0f] dark:text-white' />
              <span className='text-sm font-semibold'>Squarespace</span>
            </>
          }
          title={t('Code Injection')}
          fields={[]}
          codeLabel={t('Footer')}
          primary={t('Save')}
          primaryClass='bg-[#111] text-white dark:bg-white dark:text-[#111]'
          note={t('Shows on every page of your site')}
        />
      ),
    },
    spotlight: {
      title: t('Let visitors simply ask out loud.'),
      tail: t('Voice, right in the chat.'),
      lead: t(
        'Turn on voice calls and visitors tap Talk in the chat to speak with your agent. It answers in the voice you pick, from the same knowledge, and can even book a table during the call.',
      ),
      points: [
        t('About 1,900 voices to choose from'),
        t('The same knowledge and actions as the chat'),
        t('Visitors can still type during a call'),
        t('On Unlimited 200 and up, with a free trial in the Playground'),
      ],
      description: t(
        'A voice call in the restaurant’s website chat: the visitor asks for a table for four on Friday and the agent offers 20:00, with a note that voice calls need Unlimited 200 or higher.',
      ),
      visual: (
        <VoiceCall
          brand={BRAND}
          heard={t('Can I book a table for four on Friday?')}
          said={t(
            'Of course! Friday at 20:00 is free for four. Shall I book it?',
          )}
        />
      ),
    },
    rules: {
      title: t('Good to know.'),
      tail: t('Squarespace, in plain words.'),
      items: [
        {
          icon: GiftIcon,
          title: t('Free plan included'),
          detail: t(
            'The website chat works on every plan, including Free. Actions, such as buttons and bookings, need a paid plan.',
          ),
        },
        {
          icon: ExclamationCircleIcon,
          title: t('Squarespace plans'),
          detail: t(
            'Code Injection is only on some Squarespace plans. If you don’t see it, check your plan, or add the chat to one page with a Code block.',
          ),
        },
        {
          icon: LightningBoltIcon,
          title: t('Doesn’t slow your site'),
          detail: t(
            'The bubble shows right away, and the chat itself loads once the page has settled.',
          ),
        },
        {
          icon: RefreshIcon,
          title: t('Changes go live on their own'),
          detail: t(
            'Change the bubble’s look or greeting in Appearance and your site updates within about a minute, with no new code.',
          ),
        },
        {
          icon: MicrophoneIcon,
          title: t('Voice on Unlimited 200 and up'),
          detail: t(
            'Visitors can talk to your agent by voice on Unlimited 200 and up. On other plans, try voice in the Playground.',
          ),
        },
        {
          icon: BadgeCheckIcon,
          title: t('Your branding'),
          detail: t(
            'Use your own colors, icon and name on every plan, and hide “Powered by” on Unlimited 200 and up.',
          ),
        },
      ],
    },
    faqs: [
      {
        question: t('How do I add an AI chatbot to my Squarespace site?'),
        answer: t(
          'Copy your agent’s install snippet, open Code Injection in your Squarespace dashboard, paste it into the Footer field and click Save. The chat bubble appears on every page.',
        ),
      },
      {
        question: t('Can I add the chat to just one page?'),
        answer: t(
          'Yes. Edit the page, add a Code block and paste the iframe code. The chat then sits inside that page instead of floating over it.',
        ),
      },
      ...websiteFaqs,
    ],
    finalTitle: t('Your Squarespace site could be answering by lunchtime.'),
    finalExchange: {
      question: t('Are you open on Mondays?'),
      answer: t(
        'We’re closed on Mondays, but open from noon the rest of the week.',
      ),
    },
  };
}
